import { FC, useState } from 'react'
import {
  DataGridEditableCell,
} from '../../../core/component'
import { translate } from '../../../core'
import { Button, Checkbox, Flex, Form, Input, message, Modal, Space, Table, Typography } from 'antd'
import { InfoTooltip } from '../../../core/component'
import { DeleteOutlined, LoadingOutlined, SearchOutlined } from '@ant-design/icons'
import { ItemCategoryLevel, ItemCategoryListItem } from '../type'
import { useMutation, useQuery } from '@apollo/client'
import {
  GetItemCategoryList,
  GetItemCountByCategory,
  ItemCategoryDeleteUnusedMutation,
  ItemCategoryDisableShowOnlineMutation,
  ItemCategoryEnableShowOnlineMutation
} from '../graphql/item-category.graphql'
import { SortDirectionOptions, SortItem } from '../../../core/component/Search'
import style from './ItemCategoryList.module.scss'

const sortMapper: Record<string, SortDirectionOptions> = {
  'ascend': 'asc',
  'descend': 'desc',
}

const CategoryUsageCell: FC<{ record: ItemCategoryListItem }> = ({ record }) => {
  const { data, loading } = useQuery(GetItemCountByCategory, {
    variables: {
      request: {
        take: 1,
        skip: 0,
        ...(record.level === ItemCategoryLevel.Category ? { categoryId: [record.id] } : {}),
        ...(record.level === ItemCategoryLevel.Subcategory ? { subcategoryId: [record.id] } : {}),
        ...(record.level === ItemCategoryLevel.Subcategory2 ? { subcategory2Id: [record.id] } : {}),
      },
    },
  })

  if (loading) return <LoadingOutlined spin />
  return <>{data?.itemsFind.count ?? 0}</>
}

const levelTitleKey: Record<ItemCategoryLevel, string> = {
  [ItemCategoryLevel.Category]: 'category',
  [ItemCategoryLevel.Subcategory]: 'subcategory',
  [ItemCategoryLevel.Subcategory2]: 'subcategory2',
}

const childLevelOf: Record<ItemCategoryLevel, ItemCategoryLevel> = {
  [ItemCategoryLevel.Category]: ItemCategoryLevel.Subcategory,
  [ItemCategoryLevel.Subcategory]: ItemCategoryLevel.Subcategory2,
  [ItemCategoryLevel.Subcategory2]: ItemCategoryLevel.Subcategory2,
}

export type ItemCategoryListProps = {
  parentId?: number
  level?: ItemCategoryLevel
  onSelectionChange: (id: number, level: ItemCategoryLevel) => void
}

export const ItemCategoryList: FC<ItemCategoryListProps> = ({ parentId, level = ItemCategoryLevel.Category, onSelectionChange }) => {
  const [form] = Form.useForm()
  const [messageApi, contextHolder] = message.useMessage()
  const [pageSize, setPageSize] = useState<number>(15)
  const [currentPage, setCurrentPage] = useState<number>(0)
  const [sort, setSort] = useState<SortItem[]>()
  const [search, setSearch] = useState<string>()

  const { data, loading } = useQuery(GetItemCategoryList, {
    variables: {
      request: {
        title: search ?? '',
        take: pageSize,
        skip: currentPage * pageSize,
        parentId: parentId ?? undefined,
        level: parentId ? undefined : ItemCategoryLevel.Category,
        sort,
      }
    }
  })


  const [enableShowOnline, { loading: isEnablingShowOnline }] = useMutation(ItemCategoryEnableShowOnlineMutation, {
    refetchQueries: [GetItemCategoryList],
    onCompleted: () => {
      messageApi.success(translate('category', 'updated successfully'))
    },
    onError: (err) => messageApi.error(err.message),
  })

  const [disableShowOnline, { loading: isDisablingShowOnline }] = useMutation(ItemCategoryDisableShowOnlineMutation, {
    refetchQueries: [GetItemCategoryList],
    onCompleted: () => {
      messageApi.success(translate('category', 'updated successfully'))
    },
    onError: (err) => messageApi.error(err.message),
  })

  const [deleteUnused, { loading: isDeletingUnused }] = useMutation(ItemCategoryDeleteUnusedMutation, {
    refetchQueries: [GetItemCategoryList],
    onCompleted: () => {
      messageApi.success(translate('unused', 'categories', 'deleted_success_message'))
    },
    onError: (err) => messageApi.error(err.message),
  })

  return (
    <div className='gap-2'>
      <Flex gap={8} align='center' wrap='wrap'>
        <Input
          size='small'
          placeholder={translate('search', 'title')}
          style={{ width: 220 }}
          onChange={e => {
            setSearch(e.target.value)
            setCurrentPage(0)
          }}
          prefix={<SearchOutlined />}
        />
        {!parentId && (
          <Button
            danger
            loading={isDeletingUnused}
            icon={<DeleteOutlined />}
            onClick={() => {
              Modal.confirm({
                title: translate('delete', 'unused', 'categories'),
                content: translate('sure_message'),
                onOk: () => deleteUnused(),
              })
            }}
          >
            {translate('delete', 'unused', 'categories')}
          </Button>
        )}
      </Flex>

      <Form form={form} component={false} className='mt-2'>
        <Table<ItemCategoryListItem>
          loading={loading}
          size='small'
          title={() => (
            <Space size={4}>
              <Typography.Text strong>
                {translate(levelTitleKey[level])}
              </Typography.Text>
              <InfoTooltip contentKey='itemCategories.list' placement='right' />
            </Space>
          )}
          rowSelection={{
            type: 'radio',
            onChange(selectedRowKeys, selectedRows) {
              onSelectionChange?.(Number(selectedRowKeys[0]), selectedRows[0].level)
            },
          }}
          columns={[
            {
              dataIndex: 'id',
              title: translate('ID'),
              width: 100,
            },
            {
              dataIndex: 'title',
              title: translate('title'),
              width: 200,
              ellipsis: true,
            },
            {
              dataIndex: 'used',
              title: translate('used'),
              sorter: true,
              width: 150,
              render: (_: unknown, record: ItemCategoryListItem) =>
                record.used != null ? record.used : <CategoryUsageCell record={record} />,
            },
            {
              dataIndex: 'showOnline',
              title: translate('online'),
              width: 100,
              render: (value: boolean, record: ItemCategoryListItem) => (
                <>
                  {isEnablingShowOnline || isDisablingShowOnline
                    ? <LoadingOutlined />
                    : <Checkbox
                      checked={value}
                      onChange={() => value
                        ? disableShowOnline({ variables: { id: record.id } })
                        : enableShowOnline({ variables: { id: record.id } })}
                    />}
                </>
              ),
            },
          ]}
          dataSource={(data?.itemCategoryListFind.data ?? []).map(it => ({ ...it, key: it.id.toString() }))}
          pagination={{
            total: data?.itemCategoryListFind.count,
            position: ['bottomCenter'],
            pageSize: pageSize,
            showSizeChanger: false,
            current: currentPage + 1,
          }}
          onChange={(pagination, _, sorter) => {
            if (pagination.current)
              setCurrentPage(pagination.current - 1)

            if (pagination.pageSize)
              setPageSize(pagination.pageSize)

            if (sorter)
              setSort(
                [sorter].flat()
                  .filter(({ field }) => !!field)
                  .map(({ field, order }) => ({
                    field: field?.toString() ?? '',
                    direction: sortMapper[order ?? ''],
                  }))
              )
          }}
          rowClassName={style.editableRow}
          components={{
            body: { cell: DataGridEditableCell },
          }}
          expandable={{
            expandedRowRender: (record) => (
              <ItemCategoryList
                parentId={record.id}
                level={childLevelOf[record.level]}
                onSelectionChange={onSelectionChange}
              />
            ),
            rowExpandable: (record) => record.level !== ItemCategoryLevel.Subcategory2,
          }}
        />
      </Form>
      {contextHolder}
    </div>
  )
}
